import { useEffect } from 'react';
import notifee, { EventType } from '@notifee/react-native';
import {
  getInitialNotification,
  getMessaging,
  onMessage,
  onNotificationOpenedApp,
} from '@react-native-firebase/messaging';
import { getApp, getApps, initializeApp } from '@react-native-firebase/app';
import { config } from './firebase';
import store from '../redux/Store';
import { addNotification } from '../redux/notificationsSlice';
import ChatwootService from '../services/ChatwootService';
import { ensureDefaultNotificationChannel } from '../services/requestNotificationsOnUserGesture';
import { syncNotificationsSoon } from '../services/notificationsSync';
import { isLoginPromptNotificationContent } from '../utils/Background';
import { notificationFromPush } from '../utils/pushNotificationPayload';
import { routeNotificationPress } from '../navigation/notificationPressRouter';

const handleChatwootForegroundMessage = (remoteMessage: any) => {
  if (!ChatwootService.isChatwootAgentMessageData(remoteMessage?.data)) {
    return false;
  }

  const unreadCount = Number(remoteMessage.data.unread_count || 0);
  ChatwootService.setUnreadCount(unreadCount);
  return true;
};

const NotificationHandler = () => {
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let notifeeUnsubscribe: (() => void) | undefined;
    let openedUnsubscribe: (() => void) | undefined;

    const initializeFirebaseMessaging = async () => {
      // Ensure Firebase is initialized before using messaging
      if (getApps().length === 0) {
        console.log('Initializing Firebase in NotificationHandler...');
        await initializeApp(config);
      }
      return getMessaging(getApp());
    };

    const initializeNotifications = async () => {
      try {
        // Do NOT request permission here — wait for notification bell / Chatwoot tap.
        await ensureDefaultNotificationChannel();

        // Taps on notifications notifee displayed: pushes that arrived in
        // the foreground, and local notices such as waiter orders.
        notifeeUnsubscribe = notifee.onForegroundEvent(async event => {
          const { type, detail } = event;
          if (type === EventType.DISMISSED) {
            console.log('Notification dismissed', detail.notification);
            return;
          }
          if (type !== EventType.PRESS) return;
          console.log('Notification pressed', detail.notification);
          const data = detail.notification?.data;
          if (await routeNotificationPress(data)) return;
          const pressed = notificationFromPush({
            messageId: detail.notification?.id,
            title: detail.notification?.title,
            body: detail.notification?.body,
            data: data as Record<string, any>,
          });
          if (pressed) store.dispatch(addNotification(pressed));
        });

        // Opened from a closed state by tapping a notifee notification.
        const initialLocal = await notifee.getInitialNotification();
        if (initialLocal?.notification?.data) {
          await routeNotificationPress(initialLocal.notification.data);
        }

        // Get Firebase messaging instance with proper initialization
        const messaging = await initializeFirebaseMessaging();

        // Pushes that arrived while the app was in the background or closed
        // are displayed by the system, not by us; it hands the tapped one
        // to Firebase.
        openedUnsubscribe = onNotificationOpenedApp(
          messaging,
          remoteMessage => {
            void routeNotificationPress(remoteMessage?.data);
          },
        );
        const initialRemote = await getInitialNotification(messaging);
        if (initialRemote?.data) {
          await routeNotificationPress(initialRemote.data);
        }

        // Handle FCM messages when app is in foreground
        unsubscribe = onMessage(messaging, async remoteMessage => {
          console.log('Received FCM message:', remoteMessage);
          if (handleChatwootForegroundMessage(remoteMessage)) {
            return;
          }

          const data = remoteMessage.data as Record<string, any>;
          const title = (
            remoteMessage.notification?.title ||
            data?.title ||
            ''
          ).trim();
          const body = (
            remoteMessage.notification?.body ||
            data?.body ||
            data?.message ||
            ''
          ).trim();

          const received = notificationFromPush({
            messageId: remoteMessage.messageId,
            title,
            body,
            data,
          });
          if (!received) {
            console.log(
              'Dropping FCM with no displayable content:',
              remoteMessage.messageId,
            );
            return;
          }

          if (isLoginPromptNotificationContent({ title, body })) {
            return;
          }

          store.dispatch(addNotification(received));
          // The server keeps the notice; pull its copy (and the other
          // locations' counts) so the inbox matches what it stored.
          if (received.type === 'payment_received') {
            syncNotificationsSoon();
          }
          await notifee.displayNotification({
            id: remoteMessage.messageId || undefined,
            title: title || 'Notification',
            body,
            android: {
              channelId: 'default',
              importance: 4,
              vibrationPattern: [0, 250, 250, 250],
              pressAction: { id: 'default', launchActivity: 'default' },
            },
            data: remoteMessage.data,
          });
        });
      } catch (error) {
        console.error('Error initializing notifications:', error);
      }
    };

    initializeNotifications();

    return () => {
      if (unsubscribe) unsubscribe();
      if (notifeeUnsubscribe) notifeeUnsubscribe();
      if (openedUnsubscribe) openedUnsubscribe();
    };
  }, []);

  return null;
};

export default NotificationHandler;
